import { memo } from 'react';

import styled, { css } from '@mint-lib/styled';
import { parse } from 'date-fns';

import { StaticDateSpan } from '@myn/mui';

import { VERSION_ROW_TYPES } from '../../../../../../constants/versionsColumnsObject.js';
import { SERVER_DATE_FORMAT } from '../../../../../../features/Co2NotificationRow/Co2NotificationRow.constnts.jsx';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import { VersionsTableRow } from '../../../../../../types/versions.js';
import {
  mapRowTypeToFontSize,
  mapRowTypeToFontWeight,
} from '../../../utils/index.js';

type Props = {
  value: string | null;
  row: VersionsTableRow;
};

const VersionsDateField = memo(({ value, row }: Props) => {
  const scale = useTableScale();

  const isCurrentVersion = !!row.original.isCurrentVersion;

  const date = value ? parse(value, SERVER_DATE_FORMAT, new Date()) : null;

  return (
    <Wrapper
      depth={row.depth}
      type={row.original.type as VERSION_ROW_TYPES}
      $scale={scale}
    >
      <ValueLabel
        $scale={scale}
        type={row.original.type}
        isCurrentVersion={isCurrentVersion}
      >
        {date ? <StaticDateSpan date={date} /> : '-'}
      </ValueLabel>
    </Wrapper>
  );
});

const Wrapper = styled('div')<{
  depth: number;
  $scale: number;
  type: VERSION_ROW_TYPES;
}>`
  justify-content: flex-end;
  font-weight: ${({ type }) => mapRowTypeToFontWeight(type)};
  font-size: ${({ type, $scale }) => $scale * mapRowTypeToFontSize(type)}rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;
  overflow: visible;
`;

const ValueLabel = styled('div')<{
  $scale: number;
  type: string;
  isCurrentVersion: boolean;
}>`
  white-space: nowrap;
  cursor: pointer;
  justify-content: flex-end;
  align-items: center;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  color: ${({ isCurrentVersion, theme }) =>
    isCurrentVersion
      ? theme.palette.dataVisualization.emerald600
      : theme.palette.text.secondary};
  ${({ $scale }) => css`
    padding: 0 ${17 * $scale}px;
  `}
`;

export default VersionsDateField;
